import React, {
  type MouseEvent,
  type TouchEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import styles from "./PickADay.module.css";
import { type MonthInfo } from "../../types/date";
import { usePickADayContext } from "./PickADayContext";

interface PickADayMonthSelectorProps {
  monthsNamesList: MonthInfo[];
}

export const PickADayMonthSelector: React.FC<PickADayMonthSelectorProps> = ({
  monthsNamesList,
}) => {
  const { setScrollPercent, scrollPercent } = usePickADayContext();
  const scrollRef = useRef<HTMLDivElement>(null);
  const selectorRef = useRef<HTMLDivElement>(null);

  const [top, setTop] = useState(0);
  const [isDown, setIsDown] = useState(false);

  useEffect(() => {
    const offsetHeight: number = selectorRef.current?.offsetHeight || 0;
    const scrollHeight: number = scrollRef.current?.offsetHeight || 0;

    const height: number = scrollHeight - offsetHeight;
    const onePercent: number = height / 100;
    setTop(scrollPercent * onePercent);
  }, [scrollPercent]);

  const onMouseDown = useCallback(
    (e: MouseEvent<HTMLDivElement> | TouchEvent<HTMLDivElement>) => {
      setIsDown(true);
      onMouseMove(e, true);
    },
    [setIsDown]
  );

  const onMouseLeave = useCallback(() => {
    setIsDown(false);
  }, [setIsDown]);

  const onMouseMove = useCallback(
    (
      e: MouseEvent<HTMLDivElement> | TouchEvent<HTMLDivElement>,
      force?: boolean
    ) => {
      if (force || isDown) {
        const offsetHeight: number = selectorRef.current?.offsetHeight || 0;
        const scrollHeight: number = scrollRef.current?.offsetHeight || 0;

        const halfSelectorHeight = offsetHeight / 2;

        const y =
          (e as MouseEvent<HTMLDivElement>)?.clientY ||
          (e as TouchEvent<HTMLDivElement>)?.touches[0].clientY ||
          0;
        const minY =
          (scrollRef.current?.getBoundingClientRect().y || 0) +
          halfSelectorHeight;
        const maxY = minY + scrollHeight - offsetHeight;

        if (y > minY && y < maxY) {
          const position = y - minY;

          const height: number = scrollHeight - offsetHeight;
          const onePercent: number = height / 100;

          requestAnimationFrame(() => setScrollPercent(position / onePercent));
        }
      }
    },
    [isDown, setScrollPercent]
  );

  return (
    <div
      className={styles.PickADay__months_list}
      ref={scrollRef}
      onMouseDown={onMouseDown}
      onTouchStart={onMouseDown}
      onMouseMove={onMouseMove}
      onTouchMove={onMouseMove}
      onMouseLeave={onMouseLeave}
      onMouseUp={onMouseLeave}
      onTouchEnd={onMouseLeave}
    >
      <div
        className={styles.PickADay__selector}
        ref={selectorRef}
        style={{ top }}
      />
      {monthsNamesList.map(({ name, id }) => (
        <div key={id} className={styles.PickADay__title_month}>
          {name}
        </div>
      ))}
    </div>
  );
};
